/**
 * OwnerSignupPanel — slide-in / bottom-sheet responsive signup surface.
 *
 * Desktop / tablet-landscape (>= 768px): slide in from the right, max
 *   width 28rem, backdrop dimmer, closes on Esc / backdrop click.
 * Mobile / tablet-portrait (< 768px): renders as a bottom sheet taking
 *   ~92% of the viewport height, drag-agnostic — closes via the close
 *   button and Esc.
 *
 * Content: minimal owner signup form (nome / cognome / email /
 * password). On success, closes and navigates to the owner dashboard.
 *
 * Usage:
 *   const { open, openPanel, closePanel, Panel } = useOwnerSignupPanel();
 *   <button onClick={openPanel}>Iscriviti</button>
 *   <Panel />
 */
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";


export function useOwnerSignupPanel() {
  const [open, setOpen] = React.useState(false);
  const openPanel = React.useCallback(() => setOpen(true), []);
  const closePanel = React.useCallback(() => setOpen(false), []);
  const Panel = React.useCallback(
    () => <OwnerSignupPanel open={open} onClose={closePanel} />,
    [open, closePanel],
  );
  return { open, openPanel, closePanel, Panel };
}


export function OwnerSignupPanel({ open, onClose }) {
  // Lock body scroll while the panel is open, restore on close.
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="owner-signup-panel-title"
      data-testid="owner-signup-panel"
    >
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
        data-testid="owner-signup-panel-backdrop"
      />

      {/* Desktop / tablet-landscape · right slide-in */}
      <aside
        className="
          absolute right-0 top-0 h-full w-full max-w-md
          bg-white shadow-2xl
          hidden md:flex flex-col
          animate-slide-in-right
        "
        data-testid="owner-signup-panel-desktop"
      >
        <PanelContent onClose={onClose} />
      </aside>

      {/* Mobile / tablet-portrait · bottom sheet */}
      <aside
        className="
          absolute left-0 right-0 bottom-0 max-h-[92vh]
          bg-white rounded-t-2xl shadow-2xl
          flex md:hidden flex-col
          animate-slide-in-up
        "
        data-testid="owner-signup-panel-mobile"
      >
        <div className="pt-2 pb-1 flex justify-center">
          <div className="w-10 h-1 rounded-full bg-neutral-300" />
        </div>
        <PanelContent onClose={onClose} />
      </aside>
    </div>
  );
}


function PanelContent({ onClose }) {
  const { signupOwner } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = React.useState({
    nome: "", cognome: "", email: "", password: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.nome && form.cognome && form.email && form.password.length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await signupOwner({
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      const linked = r?.anon_linkage;
      if (linked && (linked.cases + linked.journeys) > 0) {
        toast.success("Account creato — la tua analisi è ora salvata.");
      } else {
        toast.success("Account creato. Benvenuto in ValutaGenio.");
      }
      onClose?.();
      nav("/proprietari/dashboard");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : (Array.isArray(d) && d[0]?.msg) ? d[0].msg
        : "Errore registrazione. Verifica i dati e riprova.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 py-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#B40101] font-semibold mb-1">
            La scrivania per ogni immobile
          </p>
          <h2 id="owner-signup-panel-title"
              className="font-serif text-2xl text-[#0A0A0A] leading-tight">
            Salva l'analisi nel tuo profilo
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi il pannello"
          className="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-500"
          data-testid="owner-signup-panel-close"
        >
          <X size={20} />
        </button>
      </div>

      <p className="text-sm text-neutral-600 mb-6 leading-relaxed">
        In meno di un minuto crei un profilo gratuito. Le tue analisi
        restano al tuo fianco, pronte da riprendere e aggiornare
        quando vuoi.
      </p>

      <form onSubmit={submit} className="space-y-3"
            data-testid="owner-signup-panel-form">
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block text-neutral-600 mb-1">Nome</span>
            <input
              className="vg-input"
              required
              value={form.nome}
              onChange={(e) => upd("nome", e.target.value)}
              data-testid="owner-signup-panel-nome"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-neutral-600 mb-1">Cognome</span>
            <input
              className="vg-input"
              required
              value={form.cognome}
              onChange={(e) => upd("cognome", e.target.value)}
              data-testid="owner-signup-panel-cognome"
            />
          </label>
        </div>
        <label className="block text-sm">
          <span className="block text-neutral-600 mb-1">Email</span>
          <input
            className="vg-input"
            type="email"
            required
            value={form.email}
            onChange={(e) => upd("email", e.target.value)}
            data-testid="owner-signup-panel-email"
          />
        </label>
        <label className="block text-sm">
          <span className="block text-neutral-600 mb-1">
            Password <span className="text-neutral-400">(min. 6 caratteri)</span>
          </span>
          <input
            className="vg-input"
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => upd("password", e.target.value)}
            data-testid="owner-signup-panel-password"
          />
        </label>

        {err && (
          <p role="alert"
             className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2"
             data-testid="owner-signup-panel-error">
            {err}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="
            vg-btn-primary w-full flex items-center justify-center gap-2
            disabled:opacity-60 disabled:cursor-not-allowed
          "
          data-testid="owner-signup-panel-submit"
        >
          {loading
            ? <Loader2 className="animate-spin" size={18} />
            : "Crea il mio profilo"}
        </button>
      </form>

      <p className="text-xs text-center text-neutral-500 mt-4">
        Hai già un account?{" "}
        <Link
          to="/proprietari/login"
          onClick={onClose}
          className="text-[#B40101] font-semibold hover:underline"
          data-testid="owner-signup-panel-goto-login"
        >
          Accedi
        </Link>
      </p>
    </div>
  );
}
